import { useState } from 'react';
import { Icon, colorVar } from '../components/Icons';
import { useIsMobile } from '../hooks/useIsMobile';

/* ── RESUME / ONE-PAGER ──────────────────────────────────────────────
   Link-only page (not in the sidebar) — share via /#resume.
   Everything reads from content.json so admin-panel edits flow through.
   Print styles live in index.css under @media print.
─────────────────────────────────────────────────────────────────── */

export default function Resume({ content }) {
  const isMobile = useIsMobile();
  const p = content?.profile || {};
  const stats = content?.stats || [];
  const timeline = content?.timeline || [];
  const systems = content?.systems || [];
  const skills = content?.skills || [];
  const awards = content?.awards || [];

  // "NEXT" is a forward-looking entry, not a job — it belongs in the header, not experience.
  const experience = timeline.filter(t => String(t.year).toUpperCase() !== 'NEXT');
  const nextUp = timeline.find(t => String(t.year).toUpperCase() === 'NEXT');

  const contactItems = [
    { icon: 'email', label: p.email, href: p.email ? `mailto:${p.email}` : null },
    { icon: 'phone', label: p.phone, href: p.phone ? `tel:${p.phone.replace(/[^\d+]/g, '')}` : null },
    { icon: 'pin', label: p.location },
    { icon: 'linkedin', label: 'linkedin.com/in/mk-richards', href: p.linkedin },
  ].filter(c => c.label);

  return (
    <div className="page-enter resume-sheet">
      <ResumeToolbar profile={p} />

      {/* ── Header ── */}
      <header className="resume-block" style={{ borderBottom: '2px solid var(--border2)', paddingBottom: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'flex-end', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: isMobile ? 30 : 38, fontWeight: 900, lineHeight: 1.05, color: 'var(--ink)' }}>
              {p.name || 'Makayla Richards'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--deep-rose)', marginTop: 5 }}>
              {p.title}
            </div>
            <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, color: 'var(--text3)', marginTop: 5 }}>
              {p.subtitle}
            </div>
          </div>
          {p.status && (
            <span className="chip chip-sage" style={{ fontSize: 12, padding: '5px 12px', flexShrink: 0 }}>
              {p.status}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '7px 14px' : '7px 20px', marginTop: 14 }}>
          {contactItems.map(c => {
            const inner = (
              <>
                <Icon name={c.icon} size={13} stroke="var(--deep-rose)" strokeWidth={1.9} />
                <span>{c.label}</span>
              </>
            );
            const style = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--text2)', textDecoration: 'none' };
            return c.href
              ? <a key={c.icon} href={c.href} style={style}>{inner}</a>
              : <span key={c.icon} style={style}>{inner}</span>;
          })}
        </div>
      </header>

      {/* ── Summary ── */}
      {(p.aboutBio || p.pitch) && (
        <Section title="Profile">
          {p.aboutBio && <p style={{ fontSize: 14.5, color: 'var(--text2)', lineHeight: 1.7, margin: 0 }}>{p.aboutBio}</p>}
          {p.pitch && (
            <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontStyle: 'italic', color: 'var(--deep-rose)', lineHeight: 1.6, margin: '10px 0 0' }}>
              {p.pitch}
            </p>
          )}
        </Section>
      )}

      {/* ── Headline numbers ── */}
      {stats.length > 0 && (
        <Section title="Performance Highlights">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : `repeat(${Math.min(stats.length, 5)},1fr)`, gap: 8 }}>
            {stats.map(s => (
              <div key={s.id} className="resume-stat" style={{ padding: '10px 12px', borderRadius: 10, background: colorVar(s.color, 'bg'), border: '1px solid var(--border)' }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 900, lineHeight: 1, color: colorVar(s.color, 'deep') }}>{s.value}</div>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text2)', marginTop: 4, lineHeight: 1.3 }}>{s.label}</div>
                <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Experience ── */}
      {experience.length > 0 && (
        <Section title="Experience">
          {experience.map((t, i) => (
            <div key={t.id} className="resume-block" style={{ display: 'flex', gap: 14, paddingBottom: 13, marginBottom: 13, borderBottom: i < experience.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ minWidth: 62, flexShrink: 0 }}>
                <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 12, fontWeight: 700, color: 'var(--text3)' }}>{t.year}</div>
                <div style={{ width: 26, height: 3, borderRadius: 2, background: t.color || 'var(--rose)', marginTop: 5 }} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)', marginBottom: 3 }}>{t.title}</div>
                <div style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.55 }}>{t.sub}</div>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* ── Selected work ── */}
      {systems.length > 0 && (
        <Section title="Selected Systems & Projects">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            {systems.map(s => (
              <div key={s.id} className="resume-block" style={{ padding: '11px 13px', borderRadius: 10, border: '1px solid var(--border)', borderLeft: `3px solid ${colorVar(s.color, 'deep')}`, background: 'var(--warm)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)', lineHeight: 1.3 }}>{s.title}</span>
                  {s.tag && <span className={`chip chip-${s.color === 'ink' ? 'ink' : s.color}`} style={{ flexShrink: 0 }}>{s.tag}</span>}
                </div>
                {(s.metrics || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 6 }}>
                    {s.metrics.map((m, i) => (
                      <div key={i}>
                        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 900, color: colorVar(s.color, 'deep') }}>{m.value}</span>
                        <span style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{m.sub}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Skills ── */}
      {skills.length > 0 && (
        <Section title="Core Skills">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px 24px' }}>
            {skills.map(s => (
              <div key={s.id} className="resume-block">
                <div style={{ fontSize: 13.5, fontWeight: 800, color: colorVar(s.color, 'deep'), marginBottom: 5 }}>{s.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(s.tags || []).map(t => (
                    <span key={t} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: colorVar(s.color, 'bg'), color: colorVar(s.color, 'text'), border: `1px solid ${colorVar(s.color, 'border')}` }}>{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── Awards ── */}
      {awards.length > 0 && (
        <Section title="Recognition">
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '9px 24px' }}>
            {awards.map(a => (
              <div key={a.id} className="resume-block" style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <span style={{ flexShrink: 0, marginTop: 2, lineHeight: 0 }}>
                  <Icon name={a.icon || 'trophy'} size={15} stroke="var(--deep-gold)" strokeWidth={1.9} />
                </span>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>{a.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{a.org}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ── What's next ── */}
      {(nextUp || p.whatNextText) && (
        <Section title="What's Next">
          {nextUp && <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--crimson)', marginBottom: 6 }}>{nextUp.sub || nextUp.title}</div>}
          {p.whatNextText && <p style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.65, margin: 0 }}>{p.whatNextText}</p>}
        </Section>
      )}

      <footer className="resume-block" style={{ marginTop: 22, paddingTop: 12, borderTop: '1px solid var(--border)', fontFamily: "'Courier Prime',monospace", fontSize: 11, color: 'var(--text3)', display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'space-between' }}>
        <span>Full portfolio, live metrics, and work samples, makaylarichards.me</span>
        <span>{p.email}</span>
      </footer>
    </div>
  );
}

/* ── Get-the-PDF panel ──────────────────────────────────────────────────
   The PDF is a real file at /Makayla-Richards-Resume.pdf. sms: and mailto:
   cannot carry attachments, so "text it / email it" sends the visitor a link
   to that file rather than the bytes. Nothing is collected or sent to a
   server: their own app opens, pre-addressed to them, and they hit send.
──────────────────────────────────────────────────────────────────────── */
const PDF_PATH = '/Makayla-Richards-Resume.pdf';

function ResumeToolbar({ profile }) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState(null);     // null | 'sms' | 'email'
  const [dest, setDest] = useState('');
  const [err, setErr] = useState('');
  const pdfUrl = (typeof window !== 'undefined' ? window.location.origin : 'https://makaylarichards.me') + PDF_PATH;

  const name = profile?.name || 'Makayla Richards';
  const title = profile?.title || 'Director of Social & Brand Strategy';

  const smsBody = `${name} resume: ${pdfUrl}`;
  const mailSubject = `${name}, resume`;
  const mailBody = [
    `${name}`, title, '', `Resume (PDF): ${pdfUrl}`,
    `Portfolio: https://makaylarichards.me`,
    `LinkedIn: ${profile?.linkedin || 'https://www.linkedin.com/in/mk-richards/'}`,
    '', 'Driving 12.7M views a month across 60+ stations. Murrow Award winner,',
    '2x Emmy-nominated, #1 on my team in Instagram and TikTok.',
  ].join('\n');

  const send = () => {
    const v = dest.trim();
    if (!v) { setErr(`Add your ${mode === 'sms' ? 'number' : 'email'} first.`); return; }
    if (mode === 'sms') {
      const digits = v.replace(/[^\d+]/g, '');
      if (digits.replace(/\D/g, '').length < 7) { setErr('That number looks too short.'); return; }
      window.location.href = `sms:${digits}?&body=${encodeURIComponent(smsBody)}`;
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) { setErr('Check that email address.'); return; }
      window.location.href = `mailto:${encodeURIComponent(v)}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;
    }
    setErr('');
  };

  const btn = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '9px 15px', borderRadius: 10,
    border: `1.5px solid ${active ? 'var(--deep-rose)' : 'var(--border2)'}`,
    background: active ? 'var(--blush)' : 'white',
    color: active ? 'var(--crimson)' : 'var(--text)',
    fontFamily: "'Nunito',sans-serif", fontSize: 13.5, fontWeight: 800,
    textDecoration: 'none', cursor: 'pointer', transition: 'all .16s',
  });

  return (
    <div className="no-print" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <a href={PDF_PATH} download="Makayla-Richards-Resume.pdf"
           style={{ ...btn(false), background: 'linear-gradient(135deg,var(--crimson),var(--deep-rose))', color: 'white', border: 'none', boxShadow: 'var(--shadow)' }}>
          <Icon name="resume" size={14} stroke="white" /> Download the PDF
        </a>
        <button onClick={() => { setMode(mode === 'sms' ? null : 'sms'); setDest(''); setErr(''); }} style={btn(mode === 'sms')}>
          <Icon name="phone" size={14} stroke="var(--deep-rose)" /> Text it to me
        </button>
        <button onClick={() => { setMode(mode === 'email' ? null : 'email'); setDest(''); setErr(''); }} style={btn(mode === 'email')}>
          <Icon name="email" size={14} stroke="var(--deep-rose)" /> Email it to me
        </button>
        <a href="#overview" style={btn(false)}>
          <Icon name="home" size={14} stroke="var(--deep-rose)" /> Full portfolio
        </a>
      </div>

      {mode && (
        <div style={{
          marginTop: 10, padding: 14, borderRadius: 12,
          background: 'linear-gradient(135deg,var(--blush),var(--lavender))',
          border: '1.5px solid var(--rose)', maxWidth: isMobile ? '100%' : 460,
        }}>
          <div style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--crimson)', marginBottom: 7 }}>
            {mode === 'sms' ? '// Text yourself the resume' : '// Email yourself the resume'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={dest}
              onChange={e => { setDest(e.target.value); setErr(''); }}
              onKeyDown={e => e.key === 'Enter' && send()}
              type={mode === 'sms' ? 'tel' : 'email'}
              inputMode={mode === 'sms' ? 'tel' : 'email'}
              placeholder={mode === 'sms' ? 'Your phone number' : 'Your email address'}
              aria-label={mode === 'sms' ? 'Your phone number' : 'Your email address'}
              style={{
                flex: 1, minWidth: 180, padding: '10px 13px', borderRadius: 9,
                border: '1.5px solid var(--border2)', background: 'white',
                fontFamily: "'Nunito',sans-serif", fontSize: 16, color: 'var(--text)', outline: 'none',
              }}
            />
            <button onClick={send} style={{
              padding: '10px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: 'var(--crimson)', color: 'white',
              fontFamily: "'Nunito',sans-serif", fontSize: 14, fontWeight: 800,
            }}>Send</button>
          </div>
          <div style={{ fontSize: 12, color: err ? 'var(--crimson)' : 'var(--text3)', marginTop: 7, lineHeight: 1.45 }}>
            {err || 'Opens your own messages or mail with a link to the PDF, already addressed to you. Just hit send.'}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="resume-section" style={{ marginBottom: 20 }}>
      <h2 style={{
        fontFamily: "'Courier Prime',monospace", fontSize: 11.5, fontWeight: 700,
        letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--deep-rose)',
        margin: '0 0 10px', paddingBottom: 5, borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}
